import { Component, OnInit, HostListener, signal } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import {
  FirstStageScreeningService,
  FirstStagePaper,
  FirstStageScreeningStats,
} from '../../../services/first-stage-screening.service';
import { ProjectService } from '../../../services/project.service';
import { BaseAlertComponent } from '../../alerts/base-alert/base-alert.component';
import { ScreeningCriteriaBarComponent } from '../screening-criteria-bar/screening-criteria-bar.component';
import { ProjectState } from '../../../dtos/project-state';
import { Project } from '../../../dtos/project';
import { Group } from '../../../dtos/group';
import { GroupService } from '../../../services/group.service';
import { ConfirmModalComponent } from '../../modals/confirm-modal/confirm-modal.component';
import { AgreementStatsCardComponent } from '../agreement-stats-card/agreement-stats-card.component';
import { ScreeningProgressCardComponent } from '../screening-progress-card/screening-progress-card.component';

@Component({
  selector: 'app-project-first-stage-screening',
  standalone: true,
  imports: [
    CommonModule,
    BaseAlertComponent,
    ConfirmModalComponent,
    AgreementStatsCardComponent,
    ScreeningCriteriaBarComponent,
    ScreeningProgressCardComponent,
  ],
  templateUrl: './project-first-stage-screening.component.html',
  styleUrls: ['./project-first-stage-screening.component.scss'],
})
export class ProjectFirstStageScreeningComponent implements OnInit {
  projectId = '';
  project: Project | null = null;
  group: Group | null = null;
  projectState: ProjectState = ProjectState.TITLE_ABSTRACT_SCREENING;
  projectCreatedAt = '';
  projectCompletedAt = '';
  readonly ProjectState = ProjectState;
  currentPaper: FirstStagePaper | null = null;
  stats: FirstStageScreeningStats = {
    totalPapers: 0,
    unscreenedPapers: 0,
    partiallyScreenedPapers: 0,
    includedPapers: 0,
    excludedPapers: 0,
  };
  loading = false;
  errorMessage = '';
  paperLocked = true;
  hasRequestedFirst = false;

  readonly votedPapersStack = signal<FirstStagePaper[]>([]);
  advanceConfirmOpen = false;

  get isPI(): boolean {
    return this.project?.userRole === 'PI';
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    protected location: Location,
    private screeningService: FirstStageScreeningService,
    private projectService: ProjectService,
    private groupService: GroupService,
  ) {}

  ngOnInit(): void {
    this.projectId = this.route.parent?.snapshot.paramMap.get('projectId') || '';
    if (this.projectId) {
      this.loadProjectDetails();
      this.loadStats();
      this.resumeCurrentOrWait();
    }
  }

  get progressPercentage(): number {
    if (this.stats.totalPapers === 0) {
      return 0;
    }
    const completed = this.stats.totalPapers - this.stats.unscreenedPapers;
    return (completed / this.stats.totalPapers) * 100;
  }

  get screenedCount(): number {
    return this.stats.totalPapers - this.stats.unscreenedPapers;
  }

  get totalCount(): number {
    return this.stats.totalPapers;
  }

  get includeCount(): number {
    return this.stats.includedPapers;
  }

  get excludeCount(): number {
    return this.stats.excludedPapers;
  }

  get currentPaperYear(): number {
    if (!this.currentPaper || !this.currentPaper.publicationDate) {
      return new Date().getFullYear();
    }
    return new Date(this.currentPaper.publicationDate).getFullYear();
  }

  loadProjectDetails(): void {
    this.projectService.getProjectById(this.projectId).subscribe({
      next: (project) => {
        if (project) {
          this.project = project;
          this.projectState = project.state as ProjectState;
          this.projectCreatedAt = project.createdAt || '';
          this.projectCompletedAt = project.completedAt || '';

          this.groupService.getGroupById(this.project?.groupId).subscribe({
            next: (group) => {
              this.group = group;
            },
            error: (err: any) => {
              console.error('Failed to load group details', err);
            },
          });
        }
      },
      error: (err: any) => {
        console.error('Failed to load project details', err);
      },
    });
  }

  loadStats(): void {
    this.screeningService.getStats(this.projectId).subscribe({
      next: (stats: FirstStageScreeningStats) => {
        if (stats) {
          this.stats = stats;
          if (stats.unscreenedPapers === 0) {
            this.hasRequestedFirst = true;
          }
        }
      },
      error: (err: any) => {
        console.error('Failed to load screening stats', err);
        this.errorMessage = 'Failed to load progress statistics.';
      },
    });
  }

  /**
   * On init, check if the user already holds a lock (e.g. after a page reload) and restore it.
   * If no lock is held, show the "Start Screening" button instead of auto-locking.
   */
  private resumeCurrentOrWait(): void {
    this.screeningService.getCurrentPaper(this.projectId).subscribe({
      next: (paper: FirstStagePaper | null) => {
        if (paper) {
          this.currentPaper = paper;
          this.paperLocked = true;
          this.hasRequestedFirst = true;
        }
      },
      error: () => {
        // Ignore — just show the start button
      },
    });
  }

  loadNextPaper(): void {
    this.loading = true;
    this.hasRequestedFirst = true;
    this.screeningService.getNextPaper(this.projectId).subscribe({
      next: (paper: FirstStagePaper | null) => {
        this.currentPaper = paper;
        this.paperLocked = paper != null;
        this.loading = false;
      },
      error: (err: any) => {
        if (err.status === 409) {
          this.currentPaper = null;
          this.paperLocked = false;
          this.loading = false;
          this.loadProjectDetails();
        } else {
          console.error('Failed to load next screening paper', err);
          this.errorMessage = 'Failed to load the next paper in queue.';
          this.loading = false;
        }
      },
    });
  }

  includeCurrent(): void {
    if (!this.currentPaper || this.loading) return;
    this.submitVote(this.currentPaper.id, true);
  }

  excludeCurrent(): void {
    if (!this.currentPaper || this.loading) return;
    this.submitVote(this.currentPaper.id, false);
  }

  unlockCurrent(): void {
    if (!this.currentPaper || this.loading) return;
    this.loading = true;
    this.errorMessage = '';
    this.screeningService.unlockPaper(this.projectId, this.currentPaper.id).subscribe({
      next: () => {
        this.router.navigate(['/projects', this.projectId]);
      },
      error: (err: any) => {
        console.error('Failed to unlock paper', err);
        this.router.navigate(['/projects', this.projectId]);
      },
    });
  }

  skipCurrent(): void {
    if (!this.currentPaper || this.loading) return;
    this.loading = true;
    this.errorMessage = '';
    const paperToStore = this.currentPaper;
    this.screeningService.skipPaper(this.projectId, this.currentPaper.id).subscribe({
      next: () => {
        if (paperToStore) {
          this.votedPapersStack.update((stack) => {
            if (stack.length > 0 && stack[stack.length - 1].id === paperToStore.id) return stack;
            return [...stack, paperToStore];
          });
        }
        this.loadStats();
        this.loadNextPaper();
      },
      error: (err: any) => {
        console.error('Failed to skip paper', err);
        const serverErrorMsg = typeof err.error === 'string' ? err.error : err.error?.message;
        this.errorMessage = serverErrorMsg || 'Failed to skip paper. Please try again.';
        this.loading = false;
      },
    });
  }

  private submitVote(paperId: number, include: boolean): void {
    this.loading = true;
    this.errorMessage = '';

    const paperToStore = this.currentPaper;

    this.screeningService.submitVote(this.projectId, paperId, include).subscribe({
      next: () => {
        if (paperToStore) {
          this.votedPapersStack.update((stack) => {
            if (stack.length > 0 && stack[stack.length - 1].id === paperToStore.id) return stack;
            return [...stack, paperToStore];
          });
        }
        this.loadStats();
        this.loadNextPaper();
      },
      error: (err: any) => {
        console.error('Failed to submit screening vote', err);
        const serverErrorMsg = typeof err.error === 'string' ? err.error : err.error?.message;
        this.errorMessage = serverErrorMsg || 'Failed to submit vote. Please try again.';
        this.loading = false;
      },
    });
  }

  undoLastVote(): void {
    const stack = this.votedPapersStack();
    if (stack.length === 0 || this.loading) return;
    if (this.projectState !== ProjectState.TITLE_ABSTRACT_SCREENING) return;

    this.loading = true;
    this.errorMessage = '';
    const paper = stack[stack.length - 1];

    if (this.currentPaper) {
      this.screeningService.unlockPaper(this.projectId, this.currentPaper.id).subscribe({
        next: () => {
          this.performUndoDelete(paper);
        },
        error: (err: any) => {
          console.error('Failed to unlock current paper before undo', err);
          this.performUndoDelete(paper);
        },
      });
    } else {
      this.performUndoDelete(paper);
    }
  }

  private performUndoDelete(paper: FirstStagePaper): void {
    this.screeningService.deleteVote(this.projectId, paper.id).subscribe({
      next: () => {
        this.votedPapersStack.update((s) => s.slice(0, -1));
        this.currentPaper = paper;
        this.loadStats();
        this.loading = false;
      },
      error: (err: any) => {
        console.error('Failed to delete vote', err);
        const serverErrorMsg = typeof err.error === 'string' ? err.error : err.error?.message;
        this.errorMessage =
          serverErrorMsg ||
          'Failed to undo vote. The paper may have been locked or completed by other reviewers.';
        this.loading = false;
      },
    });
  }

  advanceProject(): void {
    if (!this.isPI) return;
    this.loading = true;
    this.errorMessage = '';
    this.screeningService.advanceToFullTextRetrieval(this.projectId).subscribe({
      next: () => {
        this.advanceConfirmOpen = false;
        this.loading = false;
        this.projectService.notifyProjectUpdated();
        this.router.navigate(['/projects', this.projectId, 'full-text-retrieval']);
      },
      error: (err: any) => {
        console.error('Failed to advance project', err);
        const serverErrorMsg = typeof err.error === 'string' ? err.error : err.error?.message;
        this.errorMessage = serverErrorMsg || 'Failed to advance project. Please try again.';
        this.loading = false;
      },
    });
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    // Never hijack browser/OS shortcuts (e.g. Ctrl/Cmd+S used to "save" must not skip a paper).
    if (event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }

    const activeElement = document.activeElement;
    if (
      activeElement &&
      (activeElement.tagName === 'INPUT' ||
        activeElement.tagName === 'TEXTAREA' ||
        activeElement.tagName === 'SELECT' ||
        activeElement.hasAttribute('contenteditable'))
    ) {
      return;
    }

    const key = event.key.toLowerCase();
    if (key === 'i') {
      this.includeCurrent();
    } else if (key === 'e') {
      this.excludeCurrent();
    } else if (key === 's') {
      this.skipCurrent();
    } else if (key === 'u') {
      this.undoLastVote();
    }
  }
}
